import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  acceptAgentApplication,
  createAgent,
  deleteAgent,
  getAdminAgenciesWorkspaceLayout,
  createAgencyTableView,
  deleteAgencyTableView,
  getAgencyFields,
  getAgencyTableViews,
  getAgents,
  getAgentsMeta,
  patchAgent,
  resetAdminAgenciesWorkspaceLayout,
  updateAdminAgenciesWorkspaceLayout,
  updateAgencyTableView,
  updateAgent,
} from '@/lib/api';
import { validateAgencyContactMethods } from '@/lib/agencyContact';
import {
  agencyFieldDisplayValue,
  agencyFieldSortValue,
  buildFieldIndex,
  customFieldKey,
  isCustomFieldId,
} from '@/lib/agencyFields';
import {
  ACTIVE_VIEW_STORAGE_KEY,
  ALIGN_CLASSES,
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  RICH_AGENCY_COLUMN_IDS,
  nextAlignment,
} from '@/lib/agencyFieldConstants';
import AgencyColumnPicker from '@/components/admin/AgencyColumnPicker';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import { formatDateValue } from '@/lib/dates';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AgencyProfileForm from '@/components/admin/AgencyProfileForm';
import AdminAgenciesLayoutEditor from '@/components/admin/AdminAgenciesLayoutEditor';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  agencyProgramAbbreviation,
} from '@/lib/agencyDashboard';
import { cn } from '@/lib/utils';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronsUpDown,
  ChevronDown,
  ChevronUp,
  CircleDot,
  FileText,
  Flame,
  GripVertical,
  Languages,
  LayoutDashboard,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Save,
  Search,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';

const inputClass =
  'border-[rgba(6,43,26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

const controlFocusClass =
  'focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

const UNASSIGNED = '__unassigned__';
const PROGRAM_OPTIONS = [
  'Business Communication Pre-requisite Program',
  'French as a Second Language (FSL)',
  'French Test Preparation (TCF & TEF)',
  'Global Management in Hospitality Diploma',
  'Hospitality Operations Diploma',
  'International Esports Management Diploma',
  'Next-Gen Applied AI Mastery',
  'Video Content Creation and Social Media Management',
];

const EMPTY_FORM = {
  company_name: '',
  preferredName: '',
  website: '',
  agencyType: '',
  agent_name: '',
  contactInfo: '',
  mainContact: { name: '', preferredName: '', title: '', email: '', phone: '', wechatId: '' },
  secondaryContact: { name: '', preferredName: '', title: '', email: '', phone: '' },
  showSecondaryContact: false,
  communicationChannels: [],
  communicationChannelOther: '',
  natureOfBusiness: '',
  natureOfBusinessOther: '',
  numberOfEmployees: '',
  sin: '',
  companyRegistrationNumber: '',
  address: '',
  unitNumber: '',
  city: '',
  province: '',
  country: 'CA',
  postalCode: '',
  gstNumber: '',
  studentsSentToCanadaPast12Months: '',
  studentsAnticipatedForMccNext12Months: '',
  studentSourceCountries: [],
  targetEducationLevels: [],
  promotionalChannels: [],
  howDidYouHearAboutMcc: '',
  howDidYouHearAboutMccOther: '',
  tier: '0',
  status: 'pending',
  market: '',
  secondaryMarketRegions: [],
  secondaryMarketDraft: '',
  owner: UNASSIGNED,
  channel: '',
  email: '',
  phone: '',
  programsOfInterest: [],
  firstContactDate: '',
  lastContactDate: '',
  followupDate: '',
  createdBy: '',
  contractStartDate: '',
  contractRenewalDate: '',
  contractEndDate: '',
  internalRemarks: '',
  commissionSchedule: [{ year: '1', percent: '30' }, { year: '2', percent: '20' }],
  shortProgramCommissionPercent: '20',
};

const DEFAULT_META = {
  tiers: [0, 1, 2, 3],
  tierLabels: {
    0: 'No agreement signed and no students sent.',
    1: 'Agreement signed, OR up to 1 student.',
    2: 'Agreement signed AND 2-5 students.',
    3: 'Agreement signed AND 6+ students.',
  },
  statuses: ['pending', 'new', 'warm', 'hot', 'reserve', 'cold', 'signed'],
  markets: ['CN', 'IN', 'PH', 'TW', 'KR', 'VN', 'HK', 'INT'],
  programs: PROGRAM_OPTIONS,
  programsOfInterest: PROGRAM_OPTIONS,
  programRecords: [],
  programAbbreviations: {},
  agencyTypes: ['Domestic', 'International', 'Personal'],
  countries: [],
  owners: ['Jody', 'Harman', 'Dogukan', 'Karissa'],
};

function isValidWebsite(value) {
  const text = String(value || '').trim();
  if (!text) return true;
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function compactList(value, limit = 100) {
  return safeList(value)
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .slice(0, limit);
}

function buildGeneralContactInfo(form) {
  const rows = [
    form.communicationChannels?.length ? `Communication channels: ${compactList(form.communicationChannels).join(', ')}` : '',
    form.communicationChannelOther ? `Other communication channel: ${form.communicationChannelOther.trim()}` : '',
    form.natureOfBusiness ? `Nature of business: ${form.natureOfBusiness}` : '',
    form.natureOfBusinessOther ? `Other business type: ${form.natureOfBusinessOther.trim()}` : '',
    form.numberOfEmployees ? `Employees: ${form.numberOfEmployees}` : '',
    form.studentsSentToCanadaPast12Months ? `Students sent to Canada in past 12 months: ${form.studentsSentToCanadaPast12Months}` : '',
    form.studentsAnticipatedForMccNext12Months ? `Students anticipated for MCC in next 12 months: ${form.studentsAnticipatedForMccNext12Months}` : '',
    form.studentSourceCountries?.length ? `Top student countries: ${compactList(form.studentSourceCountries, 3).join(', ')}` : '',
    form.targetEducationLevels?.length ? `Target education levels: ${compactList(form.targetEducationLevels, 3).join(', ')}` : '',
    form.promotionalChannels?.length ? `Promotional channels: ${compactList(form.promotionalChannels).join(', ')}` : '',
    form.howDidYouHearAboutMcc ? `Heard about MCC: ${form.howDidYouHearAboutMcc}` : '',
    form.howDidYouHearAboutMccOther ? `Other source: ${form.howDidYouHearAboutMccOther.trim()}` : '',
  ];
  return rows.filter(Boolean).join('\n');
}

const STATUS_STYLES = {
  pending: 'border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#7A5A05]',
  new: 'border-[#062B1A]/15 bg-[#062B1A]/5 text-[#062B1A]/70',
  warm: 'border-amber-200 bg-amber-50 text-amber-700',
  hot: 'border-red-200 bg-red-50 text-red-700',
  reserve: 'border-orange-200 bg-orange-50 text-orange-700',
  cold: 'border-slate-200 bg-slate-50 text-slate-600',
  signed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

const DASHBOARD_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', compactLabel: 'Pending' },
  { value: 'cold', label: 'Lead (no response)', compactLabel: 'Lead' },
  { value: 'warm', label: 'Potential (in discussion)', compactLabel: 'Potential' },
  { value: 'reserve', label: 'Follow-up', compactLabel: 'Follow-up' },
  { value: 'hot', label: 'HOT', compactLabel: 'HOT' },
];

const PROVINCE_STATE_COUNTRIES = new Set(['CA', 'US']);

function labelize(value) {
  return String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Not set';
}

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeCountryCode(value) {
  const text = String(value || '').trim();
  const legacy = {
    Canada: 'CA',
    canada: 'CA',
    China: 'CN',
    china: 'CN',
    India: 'IN',
    india: 'IN',
    Philippines: 'PH',
    philippines: 'PH',
    Taiwan: 'TW',
    taiwan: 'TW',
    Vietnam: 'VN',
    vietnam: 'VN',
    'Hong Kong': 'HK',
    'United States': 'US',
  };
  return legacy[text] || text.toUpperCase();
}

function countryOptions(meta) {
  return safeList(meta.countries).filter((country) => country?.code);
}

function countryName(meta, code) {
  const normalized = countryCode(meta, code);
  const match = countryOptions(meta).find((country) => country.code === normalized);
  if (match) return match.name;
  if (normalized === 'INT') return 'International';
  return normalized || 'Not set';
}

function countryCode(meta, value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const normalized = normalizeCountryCode(text);
  const match = countryOptions(meta).find((country) => (
    String(country.code || '').toUpperCase() === normalized
    || String(country.name || '').toLocaleLowerCase('en-CA') === text.toLocaleLowerCase('en-CA')
  ));
  return match?.code || normalized;
}

function cleanCommissionSchedule(rows) {
  return safeList(rows)
    .map((row) => ({ year: Number(row.year), percent: Number(row.percent) }))
    .filter((row) => Number.isInteger(row.year) && row.year > 0 && Number.isFinite(row.percent) && row.percent >= 0 && row.percent <= 100);
}

function isRegistrationNumberRequired(agencyType) {
  return agencyType === 'Domestic' || agencyType === 'International';
}

function isValidPercent(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= 100;
}

function resolvedMarketCodes(form, meta) {
  const sourceMarkets = compactList(form.studentSourceCountries, 3)
    .map((country) => countryCode(meta, country));
  const storedPrimary = countryCode(meta, form.market);
  const storedSecondary = safeList(form.secondaryMarketRegions)
    .map((country) => countryCode(meta, country))
    .filter(Boolean);
  const fallbackMarkets = sourceMarkets;
  const chinaDefaultIsStale = storedPrimary === 'CN'
    && storedSecondary.length === 0
    && fallbackMarkets.length > 0
    && !fallbackMarkets.includes('CN');
  const useFallback = !storedPrimary || chinaDefaultIsStale;
  const primary = useFallback ? (fallbackMarkets[0] || '') : storedPrimary;
  const secondary = useFallback ? fallbackMarkets.slice(1) : storedSecondary;
  return [primary, ...secondary].filter((market, index, list) => market && list.indexOf(market) === index);
}

function makePayload(form, meta) {
  const agencyType = form.agencyType || null;
  const contactSummary = buildGeneralContactInfo(form);
  const [market = '', ...secondaryMarketRegions] = resolvedMarketCodes(form, meta);
  return {
    company_name: form.company_name.trim(),
    preferredName: form.preferredName.trim(),
    website: form.website.trim(),
    agencyType,
    agent_name: form.mainContact.name.trim() || form.agent_name.trim(),
    contactInfo: contactSummary || form.contactInfo.trim(),
    mainContact: {
      name: form.mainContact.name.trim(),
      preferredName: form.mainContact.preferredName.trim(),
      title: form.mainContact.title.trim(),
      email: form.mainContact.email.trim(),
      phone: form.mainContact.phone.trim(),
      wechatId: form.mainContact.wechatId.trim(),
    },
    secondaryContact: {
      name: form.secondaryContact.name.trim(),
      preferredName: form.secondaryContact.preferredName.trim(),
      title: form.secondaryContact.title.trim(),
      email: form.secondaryContact.email.trim(),
      phone: form.secondaryContact.phone.trim(),
    },
    sin: agencyType === 'Personal' ? form.sin.trim() : '',
    companyRegistrationNumber: isRegistrationNumberRequired(agencyType) ? form.companyRegistrationNumber.trim() : '',
    communicationChannels: compactList(form.communicationChannels),
    communicationChannelOther: form.communicationChannelOther.trim(),
    natureOfBusiness: form.natureOfBusiness.trim(),
    natureOfBusinessOther: form.natureOfBusinessOther.trim(),
    numberOfEmployees: form.numberOfEmployees.trim(),
    address: form.address.trim(),
    unitNumber: form.unitNumber.trim(),
    city: form.city.trim(),
    province: PROVINCE_STATE_COUNTRIES.has(normalizeCountryCode(form.country)) ? form.province.trim() : '',
    country: normalizeCountryCode(form.country) || null,
    studentsSentToCanadaPast12Months: form.studentsSentToCanadaPast12Months.trim(),
    studentsAnticipatedForMccNext12Months: form.studentsAnticipatedForMccNext12Months.trim(),
    studentSourceCountries: compactList(form.studentSourceCountries, 3),
    targetEducationLevels: compactList(form.targetEducationLevels, 3),
    promotionalChannels: compactList(form.promotionalChannels),
    howDidYouHearAboutMcc: form.howDidYouHearAboutMcc.trim(),
    howDidYouHearAboutMccOther: form.howDidYouHearAboutMccOther.trim(),
    tier: Number(form.tier),
    status: form.status,
    market,
    secondaryMarketRegions,
    owner: form.owner === UNASSIGNED ? null : form.owner,
    channel: form.channel.trim(),
    postalCode: form.postalCode.trim(),
    gstNumber: form.country === 'CA' ? form.gstNumber.trim() : '',
    email: form.email.trim(),
    phone: form.phone.trim(),
    programsOfInterest: compactList(form.programsOfInterest, 3),
    firstContactDate: form.firstContactDate || null,
    lastContactDate: form.lastContactDate || null,
    followupDate: form.followupDate || null,
    createdBy: form.createdBy.trim(),
    contractStartDate: form.contractStartDate || null,
    contractRenewalDate: form.contractRenewalDate || null,
    contractEndDate: form.contractEndDate || null,
    internalRemarks: form.internalRemarks.trim(),
    commissionSchedule: cleanCommissionSchedule(form.commissionSchedule),
    shortProgramCommissionPercent: Number(form.shortProgramCommissionPercent || 20),
  };
}

function agentToForm(agent) {
  const value = agent || {};
  return {
    ...EMPTY_FORM,
    ...Object.fromEntries(Object.keys(EMPTY_FORM).map((key) => [key, value[key] ?? EMPTY_FORM[key]])),
    tier: String(value.tier ?? 0),
    status: value.status || 'pending',
    owner: value.owner || UNASSIGNED,
    country: normalizeCountryCode(value.country) || 'CA',
    market: normalizeCountryCode(value.market),
    mainContact: { ...EMPTY_FORM.mainContact, ...(value.mainContact || {}) },
    secondaryContact: { ...EMPTY_FORM.secondaryContact, ...(value.secondaryContact || {}) },
    showSecondaryContact: Boolean(Object.values(value.secondaryContact || {}).some(Boolean)),
    commissionSchedule: safeList(value.commissionSchedule).length
      ? value.commissionSchedule.map((row) => ({ year: String(row.year || ''), percent: String(row.percent ?? '') }))
      : EMPTY_FORM.commissionSchedule,
    shortProgramCommissionPercent: String(value.shortProgramCommissionPercent ?? 20),
    secondaryMarketDraft: '',
    sin: '',
  };
}

function isSameValue(left, right) {
  if (Array.isArray(left) || Array.isArray(right)) {
    return JSON.stringify(left || []) === JSON.stringify(right || []);
  }
  return (left ?? null) === (right ?? null);
}

function statCardClass(accent) {
  return cn(
    'border border-[rgba(6,43,26,0.08)] shadow-sm',
    accent && 'gold-border-left',
  );
}

function statusClass(status) {
  return STATUS_STYLES[status] || 'border-[#062B1A]/12 bg-[#062B1A]/5 text-[#062B1A]/70';
}

function formatDate(value) {
  return formatDateValue(value, {
    fallback: '',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function isUnacceptedAgencySubmission(agent) {
  return Boolean(
    (agent?.publicApplication?.submittedAt || agent?.createdBy === 'Website Agency Application')
    && !agent?.applicationAcceptedAt,
  );
}

function dashboardStatus(status) {
  const normalized = String(status || 'pending').toLowerCase();
  if (normalized === 'new') return 'cold';
  if (normalized === 'signed') return 'hot';
  return DASHBOARD_STATUS_OPTIONS.some((option) => option.value === normalized) ? normalized : 'pending';
}

function marketRegion(agent, meta) {
  const regions = resolvedMarketCodes(agent, meta)
    .map((region) => countryName(meta, region))
    .filter((region, index, list) => region && region !== 'Not set' && list.indexOf(region) === index);
  return regions.join(', ') || '-';
}

function contactMethods(agent) {
  const methods = [...safeList(agent.communicationChannels)];
  if (agent.communicationChannelOther) methods.push(agent.communicationChannelOther);
  if (!methods.length && agent.channel) methods.push(agent.channel);
  if (!methods.length && (agent.mainContact?.email || agent.email)) methods.push('Email');
  if (!methods.length && (agent.mainContact?.phone || agent.phone)) methods.push('Phone');
  return [...new Set(methods.filter(Boolean))].join(', ') || 'Not set';
}

function noteSearchText(agent) {
  return (agent.noteHistory || [])
    .map((note) => [note.text, note.author].filter(Boolean).join(' '))
    .join(' ');
}

function agencyCompanyName(agent) {
  return [
    agent.preferredName,
    agent.displayName,
    agent.company_name,
  ].map((value) => String(value || '').trim()).find(Boolean) || 'Unnamed agency';
}

function agencyLegalCompanyName(agent) {
  const legalName = String(agent.company_name || '').trim();
  if (!legalName) return '';
  return legalName.toLocaleLowerCase('en-CA') === agencyCompanyName(agent).toLocaleLowerCase('en-CA')
    ? ''
    : legalName;
}

function agencyProgramNames(agent, meta) {
  return agencyProgramAbbreviations(agent, meta).join(' · ') || 'Not set';
}

// The abbreviations come from each program's Abbreviation field in Manage
// Programs, so the table shows whatever is configured there.
function agencyProgramAbbreviations(agent, meta) {
  return safeList(agent.programsOfInterest)
    .map((program) => agencyProgramAbbreviation(program, meta))
    .filter(Boolean);
}

function agencyProgramFullNames(agent) {
  return safeList(agent.programsOfInterest)
    .map((program) => String(program || '').trim())
    .filter(Boolean)
    .join(', ');
}

function agencyLatestNote(agent) {
  const latestNote = safeList(agent.noteHistory)[0];
  return latestNote?.text || agent.notes || agent.internalRemarks || 'No notes';
}

function dashboardStatusLabel(status) {
  const currentStatus = dashboardStatus(status);
  const option = DASHBOARD_STATUS_OPTIONS.find((item) => item.value === currentStatus);
  return option?.compactLabel || option?.label || labelize(currentStatus);
}

const AGENCY_TABLE_COLUMNS = [
  { id: 'companyName', label: 'Company Name', width: 190, type: 'text', value: agencyCompanyName },
  { id: 'status', label: 'Status', width: 120, type: 'text', value: (agent) => dashboardStatusLabel(agent.status) },
  { id: 'tier', label: 'Tier', width: 70, type: 'number', value: (agent) => Number(agent.tier ?? 0) },
  { id: 'location', label: 'Location', width: 105, type: 'text', value: (agent) => agent.city || '' },
  {
    id: 'marketRegion',
    label: 'Market Region',
    width: 105,
    type: 'text',
    value: (agent, meta) => {
      const value = marketRegion(agent, meta);
      return value === '-' ? '' : value;
    },
  },
  {
    id: 'programs',
    label: 'Program of Interest',
    width: 125,
    type: 'text',
    value: (agent, meta) => safeList(agent.programsOfInterest)
      .map((program) => agencyProgramAbbreviation(program, meta))
      .join(', '),
  },
  { id: 'owner', label: 'MCC Rep', width: 110, type: 'text', value: (agent) => agent.owner || '' },
  {
    id: 'contactMethod',
    label: 'Method of Contact',
    width: 120,
    type: 'text',
    value: (agent) => {
      const value = contactMethods(agent);
      return value === 'Not set' ? '' : value;
    },
  },
  { id: 'lastContactDate', label: 'Last Contact Date', width: 135, type: 'date', value: (agent) => agent.lastContactDate },
  { id: 'followupDate', label: 'Next Follow-up Date', width: 140, type: 'date', value: (agent) => agent.followupDate },
  {
    id: 'notes',
    label: 'Notes',
    width: 300,
    type: 'text',
    value: (agent) => {
      const value = agencyLatestNote(agent);
      return value === 'No notes' ? '' : value;
    },
  },
];

const DEFAULT_WORKSPACE_LAYOUT = {
  panels: [
    { id: 'stats', title: 'Summary cards', visible: true, w: 12, h: 2 },
    { id: 'applications', title: 'Agency applications', visible: true, w: 12, h: 4 },
    { id: 'filters', title: 'Search and filters', visible: true, w: 12, h: 2 },
    { id: 'table', title: 'Agencies table', visible: true, w: 12, h: 8 },
  ],
  columns: AGENCY_TABLE_COLUMNS.map((column) => ({
    id: column.id,
    label: column.label,
    visible: true,
    width: column.width,
  })),
  row_density: 'comfortable',
};

// Radix Select cannot hold an empty string value, so the default layout needs
// a sentinel of its own.
const DEFAULT_VIEW_VALUE = '__default_layout__';

const agencySortCollator = new Intl.Collator('en-CA', {
  usage: 'sort',
  numeric: true,
  sensitivity: 'base',
});

function normalizedSortValue(value, type) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  if (type === 'number') {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
  if (type === 'date') {
    const timestamp = Date.parse(String(value));
    return Number.isNaN(timestamp) ? null : timestamp;
  }
  return String(value).trim().normalize('NFKC');
}

function compareAgencyValues(leftValue, rightValue, type, direction) {
  const left = normalizedSortValue(leftValue, type);
  const right = normalizedSortValue(rightValue, type);
  if (left === null || right === null) {
    if (left === null && right === null) return 0;
    return left === null ? 1 : -1;
  }
  const result = type === 'text'
    ? agencySortCollator.compare(left, right)
    : left - right;
  return direction === 'desc' ? -result : result;
}

function sortAgencyRows(agents, sort, meta, fieldIndex) {
  if (!sort.id) return agents;
  const column = AGENCY_TABLE_COLUMNS.find((item) => item.id === sort.id);
  // Columns added from the field catalog have no bespoke accessor, so fall back
  // to reading the value generically off the record.
  const definition = fieldIndex?.byId?.[sort.id];
  if (!column && !definition) return agents;
  const readValue = column
    ? (agent) => column.value(agent, meta)
    : (agent) => agencyFieldSortValue(sort.id, agent, fieldIndex);
  const valueType = column?.type || (definition?.type === 'number' || definition?.type === 'date' ? definition.type : 'text');
  return agents
    .map((agent, index) => ({ agent, index }))
    .sort((left, right) => {
      const result = compareAgencyValues(
        readValue(left.agent),
        readValue(right.agent),
        valueType,
        sort.direction,
      );
      if (result) return result;
      const companyResult = agencySortCollator.compare(
        agencyCompanyName(left.agent),
        agencyCompanyName(right.agent),
      );
      if (companyResult) return companyResult;
      const idResult = agencySortCollator.compare(String(left.agent.id || ''), String(right.agent.id || ''));
      return idResult || left.index - right.index;
    })
    .map(({ agent }) => agent);
}

function StatCard({ label, value, icon: Icon, tone, accent }) {
  return (
    <Card className={statCardClass(accent)}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{label}</span>
          <span className={cn('rounded-full p-1.5', tone)}>
            <Icon className="size-4" />
          </span>
        </div>
        <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function AgencySubmissionRow({ agent, meta, savingCell, actionId, onPatch, onOpen, onDelete, onAccept }) {
  const application = agent.publicApplication || {};
  const displayName = agent.displayName || agent.preferredName || agent.company_name || 'Unnamed agency';
  const contact = agent.mainContact || {};
  const email = contact.email || agent.email || '';
  const isSaving = (field) => savingCell === `${agent.id}:${field}`;

  return (
    <div className="grid gap-4 border-t border-[#062B1A]/8 px-5 py-4 first:border-t-0 xl:grid-cols-[minmax(0,1.4fr)_minmax(140px,.6fr)_minmax(155px,.7fr)_minmax(155px,.7fr)_minmax(300px,auto)] xl:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onOpen(agent)}
            className="truncate text-left font-semibold text-[#062B1A] hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
          >
            {displayName}
          </button>
          <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-semibold', statusClass(agent.status))}>
            {labelize(agent.status || 'pending')}
          </span>
        </div>
        <p className="mt-1 truncate text-sm text-[#062B1A]/58">
          {[contact.name || agent.agent_name, email].filter(Boolean).join(' · ') || 'No contact details'}
        </p>
        <p className="mt-1 text-xs text-[#062B1A]/42">
          Submitted {formatDate(application.submittedAt || agent.created_at)}
          {agent.agencyType ? ` · ${agent.agencyType}` : ''}
        </p>
      </div>

      <div>
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">Owner</label>
        <Select
          value={agent.owner || UNASSIGNED}
          disabled={isSaving('owner')}
          onValueChange={(value) => onPatch(agent, 'owner', value === UNASSIGNED ? null : value)}
        >
          <SelectTrigger className={cn('h-9 w-full', inputClass)}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {safeList(meta.owners).map((owner) => <SelectItem key={owner} value={owner}>{owner}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">Follow-up date</label>
        <Input
          type="date"
          value={String(agent.followupDate || '').slice(0, 10)}
          disabled={isSaving('followupDate')}
          onChange={(event) => onPatch(agent, 'followupDate', event.target.value || null)}
          className={cn('h-9', inputClass)}
          aria-label={`Follow-up date for ${displayName}`}
        />
      </div>

      <div>
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">Pipeline status</label>
        <Select
          value={agent.status || 'pending'}
          disabled={isSaving('status')}
          onValueChange={(value) => onPatch(agent, 'status', value)}
        >
          <SelectTrigger className={cn('h-9 w-full', inputClass)}><SelectValue /></SelectTrigger>
          <SelectContent>
            {safeList(meta.statuses).map((status) => <SelectItem key={status} value={status}>{labelize(status)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-2 xl:justify-end">
        <Button type="button" variant="outline" onClick={() => onOpen(agent)} className="h-9 border-[#062B1A]/15 text-[#062B1A]">
          Review
          <ArrowRight className="ml-2 size-4" />
        </Button>
        <Button type="button" variant="outline" disabled={Boolean(actionId)} onClick={() => onDelete(agent)} className="h-9 border-red-200 text-red-700 hover:bg-red-50">
          {actionId === `delete:${agent.id}` ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Trash2 className="mr-2 size-4" />}
          Delete
        </Button>
        <Button type="button" disabled={Boolean(actionId)} onClick={() => onAccept(agent)} className="h-9 bg-[#062B1A] text-white hover:bg-[#041B10]">
          {actionId === `accept:${agent.id}` ? <Loader2 className="mr-2 size-4 animate-spin" /> : <CheckCircle2 className="mr-2 size-4" />}
          Accept
        </Button>
      </div>
    </div>
  );
}

function ColumnResizeGrip({ column, onResize }) {
  const begin = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = Number(column.width) || 140;
    const drag = (moveEvent) => {
      const next = startWidth + (moveEvent.clientX - startX);
      onResize(Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, Math.round(next))));
    };
    const finish = () => {
      window.removeEventListener('pointermove', drag);
      window.removeEventListener('pointerup', finish);
    };
    window.addEventListener('pointermove', drag);
    window.addEventListener('pointerup', finish);
  };

  return (
    <span
      role="separator"
      aria-label={`Resize ${column.label}`}
      aria-orientation="vertical"
      onPointerDown={begin}
      className="absolute right-0 top-0 z-20 h-full w-1.5 cursor-col-resize bg-[#D4AF37]/70 hover:bg-[#D4AF37]"
    />
  );
}

// Custom fields are editable straight from the table, since that is where an
// admin is when they notice a value is missing.
function AgencyCustomFieldCell({ agent, definition, saving, onPatch }) {
  const key = customFieldKey(definition?.id || '');
  const stored = agent?.customFields && typeof agent.customFields === 'object'
    ? agent.customFields[key]
    : undefined;
  const [draft, setDraft] = useState(stored ?? '');

  useEffect(() => {
    setDraft(stored ?? '');
  }, [stored]);

  if (!definition) return null;

  const commit = (value) => {
    const normalized = value === '' ? null : value;
    if (normalized === (stored ?? null)) return;
    onPatch(agent, 'customFields', { [key]: normalized }, definition.id);
  };

  const controlClass = 'h-7 w-full min-w-0 border-[#062B1A]/12 bg-white px-1 text-[10px] 2xl:text-[11px]';

  if (definition.type === 'select') {
    return (
      <Select
        value={String(draft || '')}
        disabled={saving}
        onValueChange={(value) => {
          setDraft(value);
          commit(value);
        }}
      >
        <SelectTrigger aria-label={definition.label} className={cn(controlClass, controlFocusClass)}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {(definition.options || []).map((option) => (
            <SelectItem key={option} value={option}>{option}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (definition.type === 'boolean') {
    return (
      <Select
        value={draft === true ? 'yes' : draft === false ? 'no' : ''}
        disabled={saving}
        onValueChange={(value) => {
          const next = value === 'yes';
          setDraft(next);
          commit(next);
        }}
      >
        <SelectTrigger aria-label={definition.label} className={cn(controlClass, controlFocusClass)}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type={definition.type === 'number' ? 'number' : definition.type === 'date' ? 'date' : 'text'}
      value={draft ?? ''}
      disabled={saving}
      aria-label={definition.label}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={(event) => commit(event.target.value)}
      className={cn(controlClass, controlFocusClass)}
    />
  );
}

function AgencyDashboardTable({
  agents,
  meta,
  savingCell,
  sort,
  onSort,
  onPatch,
  onOpen,
  layout,
  editMode,
  fieldIndex,
  onResizeColumn,
  onRemoveColumn,
  onReorderColumn,
  onRenameColumn,
  onAlignColumn,
}) {
  const isSaving = (agent, field) => savingCell === `${agent.id}:${field}`;
  const cellInputClass = 'h-8 w-[6.25rem] max-w-full min-w-0 border-[#062B1A]/12 bg-white px-1 text-[10px] 2xl:text-[11px]';
  const visibleColumns = safeList(layout?.columns).filter((column) => column.visible);
  const columnById = Object.fromEntries(visibleColumns.map((column, index) => [column.id, { ...column, order: index }]));
  const gridTemplateColumns = visibleColumns.map((column) => `${column.width}px`).join(' ');
  const tableMinWidth = visibleColumns.reduce((total, column) => total + Number(column.width || 0), 0);
  const rowPadding = layout?.row_density === 'compact' ? 'py-1.5' : layout?.row_density === 'spacious' ? 'py-4' : 'py-2.5';
  const genericColumns = visibleColumns.filter((column) => !RICH_AGENCY_COLUMN_IDS.has(column.id));

  // The leftmost visible column can stay put while the rest scroll sideways.
  // Cells are ordered with CSS `order`, not DOM order, but sticky positions
  // against the scroll container so it pins the visually-first column either way.
  const frozenId = layout?.freeze_first_column === false ? null : visibleColumns[0]?.id || null;
  const cellStyle = (id) => {
    const column = columnById[id];
    if (!column) return { display: 'none' };
    if (id !== frozenId) return { order: column.order };
    return { order: column.order, position: 'sticky', left: 0, zIndex: 10 };
  };
  const alignClass = (id) => ALIGN_CLASSES[columnById[id]?.align] || '';
  // A sticky cell needs its own background or the scrolling columns show through.
  const frozenCellClass = (id) => (id === frozenId
    ? 'bg-white shadow-[1px_0_0_0_rgba(6,43,26,0.10)] group-hover:bg-[#F7F8FA]'
    : '');

  return (
    <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
      <Table className="w-full table-fixed" style={{ minWidth: `${Math.max(700, tableMinWidth)}px` }}>
        <TableHeader>
          <TableRow className="grid bg-[#062B1A] hover:bg-[#062B1A]" style={{ gridTemplateColumns }}>
            {visibleColumns.map((configuredColumn) => {
              const column = AGENCY_TABLE_COLUMNS.find((item) => item.id === configuredColumn.id) || configuredColumn;
              const active = sort.id === column.id;
              const nextDirection = active && sort.direction === 'asc' ? 'descending' : 'ascending';
              return (
                <TableHead
                  key={column.id}
                  scope="col"
                  aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  onDragOver={editMode ? (event) => event.preventDefault() : undefined}
                  onDrop={editMode ? (event) => {
                    event.preventDefault();
                    onReorderColumn(event.dataTransfer.getData('text/plain'), column.id);
                  } : undefined}
                  style={cellStyle(column.id)}
                  className={cn(
                    'relative h-auto min-h-10 whitespace-normal px-1 text-center text-[10px] font-semibold uppercase leading-tight tracking-[0.035em] text-white 2xl:text-[11px]',
                    alignClass(column.id),
                    column.id === frozenId && 'bg-[#062B1A]',
                    editMode && 'ring-1 ring-inset ring-[#D4AF37]/40',
                  )}
                >
                  {editMode ? (
                    // Two rows: the controls sit above the rename box so a narrow
                    // column like Tier stays usable.
                    <div className="flex h-full flex-col gap-1 py-1">
                      <div className="flex items-center justify-between gap-0.5">
                        {/* Only the grip starts a drag, so the rename box stays clickable. */}
                        <span
                          draggable
                          onDragStart={(event) => event.dataTransfer.setData('text/plain', column.id)}
                          aria-label={`Move ${configuredColumn.label} column`}
                          className="shrink-0 cursor-grab rounded p-0.5 text-[#D4AF37] hover:bg-white/10"
                        >
                          <GripVertical aria-hidden="true" className="size-3" />
                        </span>
                        <button
                          type="button"
                          onClick={() => onAlignColumn(column.id)}
                          aria-label={`Alignment for ${configuredColumn.label}: ${configuredColumn.align || 'auto'}`}
                          title={`Alignment: ${configuredColumn.align || 'auto'} — click to change`}
                          className="shrink-0 rounded p-0.5 text-white/70 hover:bg-white/15 hover:text-white"
                        >
                          {configuredColumn.align === 'left' ? <AlignLeft aria-hidden="true" className="size-3" />
                            : configuredColumn.align === 'center' ? <AlignCenter aria-hidden="true" className="size-3" />
                            : configuredColumn.align === 'right' ? <AlignRight aria-hidden="true" className="size-3" />
                            : <AlignJustify aria-hidden="true" className="size-3 opacity-50" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemoveColumn(column.id)}
                          aria-label={`Remove ${configuredColumn.label} column`}
                          className="shrink-0 rounded p-0.5 text-white/70 hover:bg-white/15 hover:text-white"
                        >
                          <X aria-hidden="true" className="size-3" />
                        </button>
                      </div>
                      <Input
                        value={configuredColumn.label}
                        onChange={(event) => onRenameColumn(column.id, event.target.value)}
                        aria-label={`Rename ${configuredColumn.label} column`}
                        maxLength={60}
                        className="h-6 w-full min-w-0 border-white/25 bg-white/10 px-1 text-center text-[10px] font-semibold normal-case text-white placeholder:text-white/40 focus-visible:ring-[#F6D76B] 2xl:text-[11px]"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-sort-key={column.id}
                      data-sort-direction={active ? sort.direction : 'none'}
                      aria-label={`Sort by ${column.label} ${nextDirection}`}
                      onClick={() => onSort(column.id)}
                      className={cn(
                        'inline-flex w-full items-center justify-center gap-1 rounded px-0.5 py-1 text-center transition-colors hover:bg-white/10 hover:text-[#F6D76B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6D76B]',
                        active && 'text-[#F6D76B]',
                      )}
                    >
                      <span>{configuredColumn.label}</span>
                      {active ? (
                        sort.direction === 'asc'
                          ? <ChevronUp aria-hidden="true" className="size-3 shrink-0 text-[#D4AF37]" />
                          : <ChevronDown aria-hidden="true" className="size-3 shrink-0 text-[#D4AF37]" />
                      ) : (
                        <ChevronsUpDown aria-hidden="true" className="size-3 shrink-0 text-white/45" />
                      )}
                    </button>
                  )}
                  {editMode && (
                    <ColumnResizeGrip
                      column={configuredColumn}
                      onResize={(width) => onResizeColumn(column.id, width)}
                    />
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => {
            const displayName = agencyCompanyName(agent);
            const legalName = agencyLegalCompanyName(agent);
            const currentStatus = dashboardStatus(agent.status);
            const currentStatusOption = DASHBOARD_STATUS_OPTIONS.find((option) => option.value === currentStatus);
            const notes = agencyLatestNote(agent);
            const programNames = agencyProgramFullNames(agent);
            const programAbbreviations = agencyProgramAbbreviations(agent, meta);
            return (
              <TableRow key={agent.id} className="group grid bg-white hover:bg-[#F7F8FA]/80" style={{ gridTemplateColumns }}>
                <TableCell style={cellStyle('companyName')} className={cn('overflow-hidden px-1.5 align-top', rowPadding, alignClass('companyName'), frozenCellClass('companyName'))}>
                  <button
                    type="button"
                    onClick={() => onOpen(agent.id)}
                    aria-label={legalName ? `${displayName}, legal name ${legalName}` : displayName}
                    className="w-full break-words text-left leading-snug text-[#062B1A] hover:text-[#9A7410] focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
                  >
                    <span
                      data-agency-name="preferred"
                      className="line-clamp-2 text-xs font-bold hover:underline 2xl:text-sm"
                    >
                      {displayName}
                    </span>
                    {legalName && (
                      <span
                        data-agency-name="legal"
                        className="mt-0.5 line-clamp-2 text-[10px] font-normal text-[#062B1A]/48 2xl:text-[11px]"
                      >
                        {legalName}
                      </span>
                    )}
                  </button>
                </TableCell>
                <TableCell style={cellStyle('status')} className={cn('overflow-hidden px-1.5 align-top', rowPadding, alignClass('status'), frozenCellClass('status'))}>
                  <Select
                    value={currentStatus}
                    disabled={isSaving(agent, 'status')}
                    onValueChange={(value) => onPatch(agent, 'status', value)}
                  >
                    <SelectTrigger aria-label={`Status for ${displayName}`} className={cn('inline-flex h-8 w-auto min-w-[4.75rem] max-w-full gap-1 rounded-full border px-2 text-[10px] font-semibold 2xl:text-[11px]', statusClass(currentStatus), controlFocusClass)}>
                      <SelectValue>{currentStatusOption?.compactLabel || currentStatusOption?.label || labelize(currentStatus)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {DASHBOARD_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell style={cellStyle('tier')} className={cn('overflow-hidden px-0.5 align-top', rowPadding, alignClass('tier'), frozenCellClass('tier'))}>
                  <Select
                    value={String(Number(agent.tier ?? 0))}
                    disabled={isSaving(agent, 'tier')}
                    onValueChange={(value) => onPatch(agent, 'tier', Number(value))}
                  >
                    <SelectTrigger aria-label={`Tier for ${displayName}`} className={cn('inline-flex h-8 w-auto min-w-[3rem] max-w-full gap-1 px-1.5 text-[10px] 2xl:text-[11px]', inputClass)}>
                      <SelectValue>{String(Number(agent.tier ?? 0))}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {safeList(meta.tiers).map((tier) => <SelectItem key={tier} value={String(tier)}>Tier {tier}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell style={cellStyle('location')} className={cn('overflow-hidden break-words px-1.5 align-top text-[11px] text-[#062B1A]/75 2xl:text-xs', rowPadding, alignClass('location'), frozenCellClass('location'))}>
                  {agent.city || 'Not set'}
                </TableCell>
                <TableCell style={cellStyle('marketRegion')} className={cn('overflow-hidden break-words px-1.5 align-top text-[11px] text-[#062B1A]/75 2xl:text-xs', rowPadding, alignClass('marketRegion'), frozenCellClass('marketRegion'))}>
                  {marketRegion(agent, meta)}
                </TableCell>
                <TableCell style={cellStyle('programs')} className={cn('overflow-hidden px-1.5 align-top', rowPadding, alignClass('programs'), frozenCellClass('programs'))}>
                  {programAbbreviations.length ? (
                    <div
                      title={programNames || undefined}
                      aria-label={programNames || undefined}
                      className="flex flex-wrap items-center gap-1"
                    >
                      {programAbbreviations.map((abbreviation) => (
                        <Badge
                          key={abbreviation}
                          variant="outline"
                          className="border-[#062B1A]/20 bg-[#062B1A]/5 px-1.5 py-0 text-[10px] font-semibold text-[#062B1A] 2xl:text-[11px]"
                        >
                          {abbreviation}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[11px] text-[#062B1A]/40 2xl:text-xs">Not set</span>
                  )}
                </TableCell>
                <TableCell style={cellStyle('owner')} className={cn('overflow-hidden px-1.5 align-top', rowPadding, alignClass('owner'), frozenCellClass('owner'))}>
                  <Select
                    value={agent.owner || UNASSIGNED}
                    disabled={isSaving(agent, 'owner')}
                    onValueChange={(value) => onPatch(agent, 'owner', value === UNASSIGNED ? null : value)}
                  >
                    <SelectTrigger aria-label={`MCC representative for ${displayName}`} className={cn('inline-flex h-8 w-auto min-w-[4.5rem] max-w-full gap-1 px-1.5 text-[10px] 2xl:text-[11px]', inputClass)}>
                      <SelectValue>{agent.owner || '—'}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {safeList(meta.owners).map((owner) => <SelectItem key={owner} value={owner}>{owner}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell style={cellStyle('contactMethod')} className={cn('overflow-hidden px-1.5 align-top text-[11px] text-[#062B1A]/75 2xl:text-xs', rowPadding, alignClass('contactMethod'), frozenCellClass('contactMethod'))}>
                  <p className="line-clamp-3 break-words">{contactMethods(agent)}</p>
                </TableCell>
                <TableCell style={cellStyle('lastContactDate')} className={cn('overflow-hidden px-0.5 align-top', rowPadding, alignClass('lastContactDate'), frozenCellClass('lastContactDate'))}>
                  <Input
                    type="date"
                    value={String(agent.lastContactDate || '').slice(0, 10)}
                    disabled={isSaving(agent, 'lastContactDate')}
                    onChange={(event) => onPatch(agent, 'lastContactDate', event.target.value || null)}
                    className={cellInputClass}
                    aria-label={`Last contact date for ${displayName}`}
                  />
                </TableCell>
                <TableCell style={cellStyle('followupDate')} className={cn('overflow-hidden px-1 align-top', rowPadding, alignClass('followupDate'), frozenCellClass('followupDate'))}>
                  <Input
                    type="date"
                    value={String(agent.followupDate || '').slice(0, 10)}
                    disabled={isSaving(agent, 'followupDate')}
                    onChange={(event) => onPatch(agent, 'followupDate', event.target.value || null)}
                    className={cellInputClass}
                    aria-label={`Next follow-up date for ${displayName}`}
                  />
                </TableCell>
                <TableCell style={cellStyle('notes')} className={cn('overflow-hidden px-1.5 align-top', rowPadding, alignClass('notes'), frozenCellClass('notes'))}>
                  <p className="line-clamp-4 break-words text-[11px] leading-relaxed text-[#062B1A]/72 2xl:text-xs">{notes}</p>
                </TableCell>
                {/* Any column beyond the eleven with bespoke cells above renders
                    generically, so adding one needs no code here. */}
                {genericColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={cellStyle(column.id)}
                    className={cn('overflow-hidden px-1.5 align-top', rowPadding, alignClass(column.id), frozenCellClass(column.id))}
                  >
                    {isCustomFieldId(column.id) ? (
                      <AgencyCustomFieldCell
                        agent={agent}
                        definition={fieldIndex?.byId?.[column.id]}
                        saving={isSaving(agent, column.id)}
                        onPatch={onPatch}
                      />
                    ) : (
                      <p className="line-clamp-3 break-words text-[11px] leading-relaxed text-[#062B1A]/80 2xl:text-xs">
                        {agencyFieldDisplayValue(column.id, agent, fieldIndex)}
                      </p>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function AdminAgencies() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [agents, setAgents] = useState([]);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [tableSort, setTableSort] = useState({ id: '', direction: 'asc' });
  const [savingCell, setSavingCell] = useState('');
  const [submissionActionId, setSubmissionActionId] = useState('');
  const [reviewAgent, setReviewAgent] = useState(null);
  const [reviewForm, setReviewForm] = useState(null);
  const [reviewEditing, setReviewEditing] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [workspaceLayout, setWorkspaceLayout] = useState(DEFAULT_WORKSPACE_LAYOUT);
  const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [wideLayout, setWideLayout] = useState(() => window.innerWidth >= 1280);
  const [fieldIndex, setFieldIndex] = useState(() => buildFieldIndex(null));
  const [editMode, setEditMode] = useState(false);
  // Snapshot taken when edit mode opens, so Cancel can put everything back.
  const [layoutSnapshot, setLayoutSnapshot] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [defaultLayout, setDefaultLayout] = useState(DEFAULT_WORKSPACE_LAYOUT);
  // View definitions are shared; which one you are looking at is per-browser.
  const [activeViewId, setActiveViewId] = useState(() => {
    try {
      return window.localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const loadAgencies = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const [agentsResult, metaResult, layoutResult, fieldsResult, viewsResult] = await Promise.all([
        getAgents(),
        getAgentsMeta(),
      typeof getAdminAgenciesWorkspaceLayout === 'function'
        ? getAdminAgenciesWorkspaceLayout().catch(() => ({ data: DEFAULT_WORKSPACE_LAYOUT }))
        : Promise.resolve({ data: DEFAULT_WORKSPACE_LAYOUT }),
      typeof getAgencyFields === 'function'
        ? getAgencyFields().catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
      typeof getAgencyTableViews === 'function'
        ? getAgencyTableViews().catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
      ]);
      setAgents(agentsResult.data || []);
      setMeta({ ...DEFAULT_META, ...(metaResult.data || {}) });
      setFieldIndex(buildFieldIndex(fieldsResult.data));

      const baseLayout = layoutResult.data || DEFAULT_WORKSPACE_LAYOUT;
      const views = safeList(viewsResult.data?.views);
      setDefaultLayout(baseLayout);
      setSavedViews(views);
      // A view deleted by someone else falls back to the shared default.
      const active = views.find((view) => view.id === activeViewId);
      setWorkspaceLayout(active?.layout || baseLayout);
      if (activeViewId && !active) setActiveViewId('');
    } catch (err) {
      console.error(err);
      setLoadError(err?.response?.data?.detail || 'Could not load agencies');
      setAgents([]);
      toast.error(err?.response?.data?.detail || 'Could not load agencies');
    } finally {
      setLoading(false);
    }
  }, [activeViewId]);

  const beginEditMode = () => {
    setLayoutSnapshot(workspaceLayout);
    setEditMode(true);
  };

  const cancelEditMode = () => {
    if (layoutSnapshot) setWorkspaceLayout(layoutSnapshot);
    setLayoutSnapshot(null);
    setEditMode(false);
  };

  const updateColumn = useCallback((columnId, patch) => {
    setWorkspaceLayout((current) => ({
      ...current,
      columns: safeList(current.columns).map(
        (column) => (column.id === columnId ? { ...column, ...patch } : column),
      ),
    }));
  }, []);

  const alignColumn = useCallback((columnId) => {
    setWorkspaceLayout((current) => ({
      ...current,
      columns: safeList(current.columns).map((column) => (
        column.id === columnId ? { ...column, align: nextAlignment(column.align) } : column
      )),
    }));
  }, []);

  const removeColumn = useCallback((columnId) => {
    setWorkspaceLayout((current) => {
      const remaining = safeList(current.columns).filter((column) => column.id !== columnId);
      if (!remaining.length) {
        toast.error('Keep at least one column on the table.');
        return current;
      }
      return { ...current, columns: remaining };
    });
  }, []);

  const reorderColumns = useCallback((sourceId, targetId) => {
    setWorkspaceLayout((current) => {
      const columns = safeList(current.columns);
      const from = columns.findIndex((column) => column.id === sourceId);
      const to = columns.findIndex((column) => column.id === targetId);
      if (from < 0 || to < 0 || from === to) return current;
      const next = [...columns];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...current, columns: next };
    });
  }, []);

  const addColumn = useCallback((field) => {
    setWorkspaceLayout((current) => {
      const columns = safeList(current.columns);
      if (columns.some((column) => column.id === field.id)) return current;
      return {
        ...current,
        columns: [...columns, {
          id: field.id,
          label: field.label,
          visible: true,
          width: field.width || 150,
        }],
      };
    });
  }, []);

  const selectView = useCallback((viewId) => {
    const view = savedViews.find((item) => item.id === viewId);
    setActiveViewId(view ? view.id : '');
    setWorkspaceLayout(view?.layout || defaultLayout);
    try {
      if (view) window.localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, view.id);
      else window.localStorage.removeItem(ACTIVE_VIEW_STORAGE_KEY);
    } catch {
      /* a browser blocking storage should not stop the view from switching */
    }
  }, [defaultLayout, savedViews]);

  const saveAsNewView = async () => {
    const name = window.prompt('Name this view (e.g. "China Tier 1")', '');
    if (name === null) return;
    if (!name.trim()) {
      toast.error('Give the view a name.');
      return;
    }
    try {
      setLayoutSaving(true);
      const { data } = await createAgencyTableView({ name: name.trim(), layout: workspaceLayout });
      setSavedViews((current) => [...current, data].sort((left, right) => left.name.localeCompare(right.name)));
      setActiveViewId(data.id);
      setWorkspaceLayout(data.layout);
      try {
        window.localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, data.id);
      } catch { /* non-fatal */ }
      setEditMode(false);
      setLayoutSnapshot(null);
      toast.success(`View "${data.name}" saved`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not save the view');
    } finally {
      setLayoutSaving(false);
    }
  };

  const deleteActiveView = async () => {
    const view = savedViews.find((item) => item.id === activeViewId);
    if (!view) return;
    try {
      setLayoutSaving(true);
      await deleteAgencyTableView(view.id);
      setSavedViews((current) => current.filter((item) => item.id !== view.id));
      setActiveViewId('');
      setWorkspaceLayout(defaultLayout);
      try {
        window.localStorage.removeItem(ACTIVE_VIEW_STORAGE_KEY);
      } catch { /* non-fatal */ }
      setEditMode(false);
      setLayoutSnapshot(null);
      toast.success(`View "${view.name}" deleted`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete the view');
    } finally {
      setLayoutSaving(false);
    }
  };

  const saveWorkspaceLayout = async () => {
    // Saving updates whichever layout is on screen: the active view, or the
    // shared default when no view is selected.
    const activeView = savedViews.find((item) => item.id === activeViewId);
    try {
      setLayoutSaving(true);
      if (activeView) {
        const { data } = await updateAgencyTableView(activeView.id, {
          name: activeView.name,
          layout: workspaceLayout,
        });
        setSavedViews((current) => current.map((item) => (item.id === data.id ? data : item)));
        setWorkspaceLayout(data.layout);
        setLayoutEditorOpen(false);
        setEditMode(false);
        setLayoutSnapshot(null);
        toast.success(`View "${data.name}" updated`);
        return;
      }
      const { data } = await updateAdminAgenciesWorkspaceLayout(workspaceLayout);
      setWorkspaceLayout(data);
      setDefaultLayout(data);
      setLayoutEditorOpen(false);
      setEditMode(false);
      setLayoutSnapshot(null);
      toast.success('Agencies workspace layout saved for everyone');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not save workspace layout');
    } finally {
      setLayoutSaving(false);
    }
  };

  const resetWorkspaceLayout = async () => {
    try {
      setLayoutSaving(true);
      const { data } = await resetAdminAgenciesWorkspaceLayout();
      setWorkspaceLayout(data);
      toast.success('Agencies workspace restored to default');
    } catch (err) {
      console.error(err);
      toast.error('Could not reset workspace layout');
    } finally {
      setLayoutSaving(false);
    }
  };

  useEffect(() => {
    if (!authLoading) loadAgencies();
  }, [authLoading, loadAgencies]);

  useEffect(() => {
    const id = window.setTimeout(() => setQ(searchInput.trim().toLowerCase()), 250);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const update = () => setWideLayout(window.innerWidth >= 1280);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const dashboardAgents = useMemo(
    () => agents.filter((agent) => !isUnacceptedAgencySubmission(agent)),
    [agents],
  );

  // `cellId` lets custom fields, which all patch the same `customFields` key,
  // show their saving state on the individual cell rather than the whole column.
  const patchCell = useCallback(async (agent, field, value, cellId) => {
    const previousValue = agent[field] ?? null;
    const isCustom = field === 'customFields';
    if (!isCustom && isSameValue(previousValue, value)) return;
    const cellKey = `${agent.id}:${cellId || field}`;
    const optimistic = isCustom
      ? { ...(agent.customFields || {}), ...value }
      : value;

    setSaveError('');
    setSavingCell(cellKey);
    setAgents((prev) => prev.map((item) => (
      item.id === agent.id ? { ...item, [field]: optimistic } : item
    )));

    try {
      const { data } = await patchAgent(agent.id, { [field]: value });
      setAgents((prev) => prev.map((item) => (
        item.id === agent.id ? { ...item, ...(data || {}) } : item
      )));
    } catch (err) {
      console.error(err);
      setAgents((prev) => prev.map((item) => (
        item.id === agent.id ? { ...item, [field]: previousValue } : item
      )));
      setSaveError("Couldn't save - try again");
      toast.error("Couldn't save - try again");
    } finally {
      setSavingCell('');
    }
  }, []);

  const filteredAgents = useMemo(() => {
    const matchesSearch = (agent) => {
      if (!q) return true;
      return [
        agent.company_name,
        agent.preferredName,
        agent.companyRegistrationNumber,
        agent.agent_name,
        agent.mainContact?.name,
        agent.mainContact?.email,
        agent.mainContact?.phone,
        agent.mainContact?.wechatId,
        agent.contactInfo,
        agent.email,
        agent.phone,
        agent.channel,
        agent.city,
        countryName(meta, agent.country),
        resolvedMarketCodes(agent, meta).map((market) => countryName(meta, market)).join(' '),
        agent.address,
        agent.owner,
        agent.notes,
        noteSearchText(agent),
      ].join(' ').toLowerCase().includes(q);
    };

    const matchesFilter = (agent) => {
      if (activeFilter === 'all') return true;
      const [type, value] = activeFilter.split(':');
      if (type === 'status') return dashboardStatus(agent.status) === value;
      if (type === 'tier') return String(agent.tier) === value;
      if (type === 'program') return (agent.programsOfInterest || []).includes(value);
      if (type === 'agencyType') return agent.agencyType === value;
      if (type === 'owner') return (agent.owner || UNASSIGNED) === value;
      if (type === 'market') return resolvedMarketCodes(agent, meta).includes(value);
      return true;
    };

    return dashboardAgents.filter((agent) => matchesSearch(agent) && matchesFilter(agent));
  }, [activeFilter, dashboardAgents, meta, q]);

  const sortedAgents = useMemo(
    () => sortAgencyRows(filteredAgents, tableSort, meta, fieldIndex),
    [fieldIndex, filteredAgents, meta, tableSort],
  );

  const updateTableSort = (columnId) => {
    setTableSort((current) => (
      current.id === columnId
        ? { id: columnId, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { id: columnId, direction: 'asc' }
    ));
  };

  const stats = useMemo(() => ({
    total: dashboardAgents.length,
    hot: dashboardAgents.filter((agent) => agent.status === 'hot').length,
    followup: dashboardAgents.filter((agent) => agent.status === 'warm').length,
    reserve: dashboardAgents.filter((agent) => agent.status === 'reserve').length,
    pending: dashboardAgents.filter((agent) => agent.status === 'pending').length,
    french: dashboardAgents.filter((agent) => (agent.programsOfInterest || []).some((program) => /french/i.test(program))).length,
    signed: dashboardAgents.filter((agent) => agent.status === 'signed').length,
  }), [dashboardAgents]);

  const agencySubmissions = useMemo(() => agents
    .filter(isUnacceptedAgencySubmission)
    .sort((left, right) => String(right.publicApplication?.submittedAt || right.created_at || '')
      .localeCompare(String(left.publicApplication?.submittedAt || left.created_at || ''))), [agents]);
  const newSubmissionCount = agencySubmissions.filter((agent) => agent.status === 'pending').length;

  const filterPills = useMemo(() => {
    const ownerSet = new Set(dashboardAgents.map((agent) => agent.owner || UNASSIGNED));
    const marketSet = new Set(dashboardAgents.flatMap((agent) => resolvedMarketCodes(agent, meta)));
    const programSet = new Set(dashboardAgents.flatMap((agent) => agent.programsOfInterest || []));
    const agencyTypeSet = new Set(dashboardAgents.map((agent) => agent.agencyType).filter(Boolean));
    return [
      { id: 'all', label: 'All' },
      ...DASHBOARD_STATUS_OPTIONS.map((status) => ({ id: `status:${status.value}`, label: status.label })),
      ...meta.tiers.map((tier) => ({ id: `tier:${tier}`, label: `Tier ${tier}` })),
      ...meta.programs
        .filter((program) => programSet.has(program))
        .map((program) => ({
          id: `program:${program}`,
          label: agencyProgramAbbreviation(program, meta),
          title: program,
        })),
      ...meta.agencyTypes
        .filter((type) => agencyTypeSet.has(type))
        .map((type) => ({ id: `agencyType:${type}`, label: type })),
      ...meta.owners
        .filter((owner) => ownerSet.has(owner))
        .map((owner) => ({ id: `owner:${owner}`, label: owner })),
      ...(ownerSet.has(UNASSIGNED) ? [{ id: `owner:${UNASSIGNED}`, label: 'Unassigned' }] : []),
      ...meta.markets
        .filter((market) => marketSet.has(market))
        .map((market) => ({ id: `market:${market}`, label: countryName(meta, market) })),
    ];
  }, [dashboardAgents, meta]);

  const hasActiveFilters = activeFilter !== 'all' || !!q;
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setForm(EMPTY_FORM);
  };

  const openReview = (agent) => {
    setReviewAgent(agent);
    setReviewForm(agentToForm(agent));
    setReviewEditing(false);
  };

  const closeReview = () => {
    if (reviewSaving || submissionActionId) return;
    setReviewAgent(null);
    setReviewForm(null);
    setReviewEditing(false);
  };

  const handleReviewSave = async () => {
    const payload = makePayload(reviewForm, meta);
    if (!payload.company_name) {
      toast.error('Company name is required');
      return;
    }
    try {
      setReviewSaving(true);
      const { data } = await updateAgent(reviewAgent.id, payload);
      setAgents((prev) => prev.map((item) => item.id === data.id ? { ...item, ...data } : item));
      setReviewAgent(data);
      setReviewForm(agentToForm(data));
      setReviewEditing(false);
      toast.success('Application updated');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not update application');
    } finally {
      setReviewSaving(false);
    }
  };

  const handleDeleteSubmission = async (agent) => {
    const name = agent.displayName || agent.preferredName || agent.company_name || 'this agency application';
    if (!window.confirm(`Delete ${name}? This application cannot be recovered.`)) return;
    try {
      setSubmissionActionId(`delete:${agent.id}`);
      await deleteAgent(agent.id);
      setAgents((prev) => prev.filter((item) => item.id !== agent.id));
      if (reviewAgent?.id === agent.id) setReviewAgent(null);
      toast.success('Agency application deleted');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete application');
    } finally {
      setSubmissionActionId('');
    }
  };

  const handleAcceptSubmission = async (agent) => {
    try {
      setSubmissionActionId(`accept:${agent.id}`);
      const { data } = await acceptAgentApplication(agent.id);
      setAgents((prev) => prev.map((item) => item.id === data.id ? { ...item, ...data } : item));
      if (reviewAgent?.id === agent.id) setReviewAgent(null);
      toast.success('Application accepted and moved to the agency dashboard');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not accept application');
    } finally {
      setSubmissionActionId('');
    }
  };

  const handleCreate = async () => {
    const payload = makePayload(form, meta);
    if (!payload.company_name) {
      toast.error('Company name is required');
      return;
    }
    if (!payload.country || !payload.city) {
      toast.error('Company country and city are required');
      return;
    }
    const contactError = validateAgencyContactMethods({
      communicationChannels: payload.communicationChannels,
      companyPhone: payload.phone,
      companyEmail: payload.email,
      mainContact: payload.mainContact,
      secondaryContact: payload.secondaryContact,
    });
    if (contactError) {
      toast.error(contactError);
      return;
    }
    if (!isValidWebsite(payload.website)) {
      toast.error('Company website must start with http:// or https://');
      return;
    }
    if (form.sin.trim() && payload.agencyType !== 'Personal') {
      toast.error('Set Type of agency to Personal to save a SIN number');
      return;
    }
    if (form.gstNumber.trim() && normalizeCountryCode(form.country) !== 'CA') {
      toast.error('GST number is only available for Canadian agencies');
      return;
    }
    if (!isValidPercent(payload.shortProgramCommissionPercent)) {
      toast.error('Short programs percentage must be between 0 and 100');
      return;
    }
    const filledCommissionRows = safeList(form.commissionSchedule).filter((row) => String(row.year || row.percent || '').trim());
    if (payload.commissionSchedule.length !== filledCommissionRows.length) {
      toast.error('Commission rows need a positive year and a percentage from 0 to 100');
      return;
    }
    try {
      setCreating(true);
      const res = await createAgent(payload);
      toast.success('Agency created');
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      navigate(`/admin/agencies/${res.data.id}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not create agency');
    } finally {
      setCreating(false);
    }
  };

  const panelById = Object.fromEntries(
    safeList(workspaceLayout.panels).map((panel, index) => [panel.id, { ...panel, order: index }]),
  );
  const panelStyle = (id) => ({
    order: panelById[id]?.order ?? 99,
    gridColumn: wideLayout ? `span ${panelById[id]?.w || 12}` : '1 / -1',
    minHeight: `${Math.max(0, (panelById[id]?.h || 1) * 34)}px`,
  });

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            <Building2 className="size-6 text-[#D4AF37]" />
            Agencies
          </h2>
          <p className="text-sm text-[#062B1A]/60">
            Track partner pipeline status, follow-ups, programs of interest, and ownership from one compact table.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editMode ? (
            <>
              <AgencyColumnPicker
                fieldIndex={fieldIndex}
                usedIds={safeList(workspaceLayout.columns).map((column) => column.id)}
                onAddColumn={addColumn}
                onFieldCreated={(field) => setFieldIndex((current) => buildFieldIndex({
                  catalog: current.catalog,
                  custom: [...current.custom, field],
                  types: current.types,
                  max_custom_fields: current.maxCustomFields,
                }))}
              />
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#D4AF37]/45 px-2.5 py-1.5 text-xs font-semibold text-[#8A6508]">
                <Checkbox
                  checked={workspaceLayout.freeze_first_column !== false}
                  onCheckedChange={(checked) => setWorkspaceLayout((current) => ({
                    ...current,
                    freeze_first_column: checked === true,
                  }))}
                />
                Freeze first column
              </label>
              <Button variant="outline" onClick={() => setLayoutEditorOpen(true)} className="border-[#D4AF37]/45 text-[#8A6508]">
                <Settings2 className="mr-2 size-4" />
                Panels
              </Button>
              <Button variant="outline" onClick={saveAsNewView} disabled={layoutSaving} className="border-[#062B1A]/20">
                <Plus className="mr-2 size-4" />
                Save as view
              </Button>
              <Button variant="ghost" onClick={cancelEditMode} disabled={layoutSaving}>
                Cancel
              </Button>
              <Button
                onClick={saveWorkspaceLayout}
                disabled={layoutSaving}
                className="bg-[#062B1A] text-white hover:bg-[#041B10]"
              >
                <Save className="mr-2 size-4" />
                {layoutSaving ? 'Saving...' : activeViewId ? 'Update view' : 'Save layout'}
              </Button>
            </>
          ) : (
            <>
              <PortalEmailComposer
                audience="agencies"
                recipients={sortedAgents.slice(0, 100)}
                buttonLabel={sortedAgents.length > 100
                  ? 'Email first 100 filtered'
                  : sortedAgents.length ? `Email filtered (${sortedAgents.length})` : 'Email agencies'}
                disabled={sortedAgents.length === 0}
                buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
              />
              <Select value={activeViewId || DEFAULT_VIEW_VALUE} onValueChange={(value) => selectView(value === DEFAULT_VIEW_VALUE ? '' : value)}>
                <SelectTrigger aria-label="Table view" className="h-9 w-auto min-w-[9rem] gap-2 border-[#062B1A]/15 text-xs font-semibold">
                  <LayoutDashboard className="size-3.5 text-[#8A6508]" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEFAULT_VIEW_VALUE}>Default layout</SelectItem>
                  {savedViews.map((view) => (
                    <SelectItem key={view.id} value={view.id}>{view.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeViewId && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={deleteActiveView}
                  disabled={layoutSaving}
                  aria-label="Delete this view"
                  className="text-[rgb(153,27,27)] hover:bg-[rgba(220,38,38,0.06)]"
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
              <Button variant="outline" onClick={beginEditMode} className="border-[#D4AF37]/45 text-[#8A6508]">
                <Settings2 className="mr-2 size-4" />
                Customize table
              </Button>
              <Button onClick={openCreate} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                <Plus className="mr-2 size-4" />
                Add agency
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
      {panelById.stats?.visible !== false && <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7" style={panelStyle('stats')}>
        <StatCard label="Total" value={stats.total} icon={Building2} tone="bg-[#062B1A]/5 text-[#062B1A]" />
        <StatCard label="Hot" value={stats.hot} icon={Flame} tone="bg-red-50 text-red-700" />
        <StatCard label="Following up" value={stats.followup} icon={CalendarClock} tone="bg-amber-50 text-amber-700" accent />
        <StatCard label="Reserve" value={stats.reserve} icon={MoreHorizontal} tone="bg-orange-50 text-orange-700" />
        <StatCard label="Pending" value={stats.pending} icon={CircleDot} tone="bg-[#D4AF37]/12 text-[#7A5A05]" />
        <StatCard label="French interest" value={stats.french} icon={Languages} tone="bg-[#D4AF37]/12 text-[#7A5A05]" accent />
        <StatCard label="Signed" value={stats.signed} icon={CheckCircle2} tone="bg-emerald-50 text-emerald-700" />
      </div>}

      {(loadError || saveError) && (
        <Alert className="border-red-200 bg-red-50 text-red-800" style={{ gridColumn: '1 / -1', order: 50 }}>
          <AlertTriangle className="size-4" />
          <AlertTitle>{loadError ? 'Agencies could not load' : 'Save failed'}</AlertTitle>
          <AlertDescription>{loadError || saveError}</AlertDescription>
        </Alert>
      )}

      {panelById.applications?.visible !== false && <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm" style={panelStyle('applications')}>
        <div className="flex flex-col gap-2 border-b border-[#062B1A]/8 bg-[#F7F8FA]/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              <FileText className="size-4 text-[#D4AF37]" />
              {panelById.applications?.title || 'Agency applications'}
              {newSubmissionCount > 0 && (
                <span className="rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-xs font-semibold text-[#7A5A05]">
                  {newSubmissionCount} new
                </span>
              )}
            </h3>
            <p className="mt-1 text-sm text-[#062B1A]/55">Assign an owner, schedule follow-up, and move new submissions through the pipeline.</p>
          </div>
          <span className="text-xs text-[#062B1A]/45">{agencySubmissions.length} submitted online</span>
        </div>

        {loading ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 2 }).map((_, index) => <div key={index} className="h-16 animate-pulse rounded-lg bg-[#F7F8FA]" />)}
          </div>
        ) : agencySubmissions.length ? (
          <div>
            {agencySubmissions.map((agent) => (
              <AgencySubmissionRow
                key={agent.id}
                agent={agent}
                meta={meta}
                savingCell={savingCell}
                actionId={submissionActionId}
                onPatch={patchCell}
                onOpen={openReview}
                onDelete={handleDeleteSubmission}
                onAccept={handleAcceptSubmission}
              />
            ))}
          </div>
        ) : (
          <div className="px-5 py-8 text-center text-sm text-[#062B1A]/45">No online agency applications yet.</div>
        )}
      </Card>}

      {panelById.filters?.visible !== false && <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm" style={panelStyle('filters')}>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="relative max-w-xl flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
              <Input
                type="search"
                aria-label="Search agencies"
                placeholder="Search company, contact, city, notes..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className={cn('pl-9', inputClass)}
              />
            </div>
            <div className="text-xs text-[#062B1A]/60">
              {loading ? 'Loading agencies...' : `${filteredAgents.length} of ${dashboardAgents.length} agencies`}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {filterPills.map((filter) => {
              const active = activeFilter === filter.id;
              return (
                <Button
                  key={filter.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveFilter(filter.id)}
                  title={filter.title}
                  aria-label={filter.title || undefined}
                  className={cn(
                    'h-8 rounded-full border-[#062B1A]/15 px-3 text-xs text-[#062B1A] hover:bg-[#062B1A]/5',
                    active && 'border-[#D4AF37] bg-[#062B1A] text-white hover:bg-[#041B10]',
                  )}
                >
                  {filter.label}
                </Button>
              );
            })}
            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 rounded-full px-3 text-xs text-[#062B1A]/65"
                onClick={() => {
                  setActiveFilter('all');
                  setSearchInput('');
                  setQ('');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>}

      {panelById.table?.visible !== false && <div style={panelStyle('table')} className="min-w-0 overflow-x-auto">{loading ? (
        <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="space-y-3 p-5">
            <div className="h-11 animate-pulse rounded bg-[#062B1A]/10" />
            {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-14 animate-pulse rounded bg-[#F7F8FA]" />)}
          </CardContent>
        </Card>
      ) : filteredAgents.length === 0 ? (
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <Building2 className="mb-3 size-12 text-[#062B1A]/15" />
            <h3 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              {hasActiveFilters ? 'No agencies match this filter' : 'No agencies yet'}
            </h3>
            <p className="mt-2 max-w-md text-sm text-[#062B1A]/50">
              {hasActiveFilters
                ? 'Try a different status, tier, owner, market, or search term.'
                : 'Add an agency to start building the partner pipeline.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <AgencyDashboardTable
          agents={sortedAgents}
          meta={meta}
          savingCell={savingCell}
          sort={tableSort}
          onSort={updateTableSort}
          onPatch={patchCell}
          onOpen={(id) => navigate(`/admin/agencies/${id}`)}
          layout={workspaceLayout}
          editMode={editMode}
          fieldIndex={fieldIndex}
          onResizeColumn={(id, width) => updateColumn(id, { width })}
          onRemoveColumn={removeColumn}
          onReorderColumn={reorderColumns}
          onRenameColumn={(id, label) => updateColumn(id, { label })}
          onAlignColumn={alignColumn}
        />
      )}</div>}
      </div>

      <AdminAgenciesLayoutEditor
        open={layoutEditorOpen}
        onOpenChange={setLayoutEditorOpen}
        layout={workspaceLayout}
        setLayout={setWorkspaceLayout}
        onSave={saveWorkspaceLayout}
        onReset={resetWorkspaceLayout}
        saving={layoutSaving}
      />

      <Dialog open={Boolean(reviewAgent)} onOpenChange={(open) => (open ? null : closeReview())}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Agency application review</DialogTitle>
          </DialogHeader>

          {reviewAgent && reviewForm && (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 rounded-xl border border-[#062B1A]/10 bg-[#F7F8FA]/70 p-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#062B1A]">
                    {reviewAgent.displayName || reviewAgent.preferredName || reviewAgent.company_name || 'Unnamed agency'}
                  </h3>
                  <p className="mt-1 text-sm text-[#062B1A]/55">
                    Submitted {formatDate(reviewAgent.publicApplication?.submittedAt || reviewAgent.created_at)}
                  </p>
                </div>
                <span className={cn('w-fit rounded-full border px-2.5 py-1 text-xs font-semibold', statusClass(reviewAgent.status))}>
                  {labelize(reviewAgent.status || 'pending')}
                </span>
              </div>

              {reviewEditing ? (
                <AgencyProfileForm
                  form={reviewForm}
                  setForm={setReviewForm}
                  meta={meta}
                  inputClass={inputClass}
                  customFields={fieldIndex.custom}
                />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    ['Legal/company name', reviewAgent.company_name],
                    ['Preferred name', reviewAgent.preferredName],
                    ['Agency type', reviewAgent.agencyType],
                    ['Website', reviewAgent.website],
                    ['Main contact', reviewAgent.mainContact?.name || reviewAgent.agent_name],
                    ['Email', reviewAgent.mainContact?.email || reviewAgent.email],
                    ['Phone', reviewAgent.mainContact?.phone || reviewAgent.phone],
                    ['Location', [reviewAgent.city, reviewAgent.province, countryName(meta, reviewAgent.country)].filter(Boolean).join(', ')],
                    [
                      'Programs of interest',
                      safeList(reviewAgent.programsOfInterest)
                        .map((program) => agencyProgramAbbreviation(program, meta))
                        .join(' · '),
                      safeList(reviewAgent.programsOfInterest).join(', '),
                    ],
                    ['How they heard about MCC', reviewAgent.howDidYouHearAboutMcc],
                  ].map(([label, value, fullValue]) => (
                    <div key={label} className="rounded-lg border border-[#062B1A]/8 p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">{label}</p>
                      <p
                        title={fullValue || undefined}
                        aria-label={fullValue || undefined}
                        className="mt-1 break-words text-sm text-[#062B1A]"
                      >
                        {value || 'Not provided'}
                      </p>
                    </div>
                  ))}
                  <div className="rounded-lg border border-[#062B1A]/8 p-3 sm:col-span-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">Application details</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#062B1A]">
                      {reviewAgent.contactInfo || 'No additional details provided.'}
                    </p>
                  </div>
                </div>
              )}

              <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  disabled={reviewSaving || Boolean(submissionActionId)}
                  onClick={() => handleDeleteSubmission(reviewAgent)}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                >
                  {submissionActionId === `delete:${reviewAgent.id}` ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Trash2 className="mr-2 size-4" />}
                  Delete application
                </Button>
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  {reviewEditing ? (
                    <>
                      <Button type="button" variant="outline" disabled={reviewSaving} onClick={() => { setReviewForm(agentToForm(reviewAgent)); setReviewEditing(false); }}>
                        Cancel edit
                      </Button>
                      <Button type="button" disabled={reviewSaving} onClick={handleReviewSave} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                        {reviewSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
                        Save changes
                      </Button>
                    </>
                  ) : (
                    <Button type="button" variant="outline" disabled={Boolean(submissionActionId)} onClick={() => setReviewEditing(true)}>
                      <Pencil className="mr-2 size-4" />
                      Edit
                    </Button>
                  )}
                  <Button type="button" disabled={reviewSaving || Boolean(submissionActionId)} onClick={() => handleAcceptSubmission(reviewAgent)} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                    {submissionActionId === `accept:${reviewAgent.id}` ? <Loader2 className="mr-2 size-4 animate-spin" /> : <CheckCircle2 className="mr-2 size-4" />}
                    Accept application
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(open) => (open ? setCreateOpen(true) : closeCreate())}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Add agency</DialogTitle>
          </DialogHeader>

          <AgencyProfileForm
            form={form}
            setForm={setForm}
            meta={meta}
            inputClass={inputClass}
            customFields={fieldIndex.custom}
          />

          <DialogFooter>
            <Button variant="outline" onClick={closeCreate} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {creating ? 'Creating...' : 'Create agency'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
